/**
 * Lista de categorias navegável — o miolo do menu lateral.
 *
 * O MESMO componente serve as duas telas, de propósito: no celular ele vai
 * dentro do drawer (`MenuDrawerIsland`), no computador ele é a barra lateral
 * fixa da home. Duas cópias divergiriam na primeira categoria nova.
 *
 * Tem busca de categoria (loja com 25+ categorias — churrascaria, sushi — era
 * rolagem infinita até achar "Sobremesas") e marca onde o cliente está:
 * um IntersectionObserver acompanha as seções de `ProductCardsIsland` e
 * acende a categoria visível.
 *
 * "Destaques" abre a lista quando a loja tem produtos em destaque. Ele NÃO é
 * uma categoria do cadastro — é o carrossel do topo da home, que some quando
 * não há destaque nenhum; por isso a entrada é condicional e vive fora do
 * `map` das categorias.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MenuCategory } from '../types';
import { useI18n } from '../i18n';

interface Props {
  categories: MenuCategory[];
  /** Chamado depois de navegar — o drawer usa pra se fechar. */
  onNavigate?: () => void;
  /** Mostra o campo de busca de categoria (esconde em lista curta). */
  comBusca?: boolean;
}

/** Remove acento pra busca casar "cafe" com "Café". */
function normalizar(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function CategoryNav({ categories, onNavigate, comBusca = true }: Props) {
  const { t, tr } = useI18n();
  const [busca, setBusca] = useState('');
  const [ativa, setAtiva] = useState(0);
  const listaRef = useRef<HTMLDivElement>(null);

  const visiveis = useMemo(
    () => categories.filter((c) => c.items && c.items.length > 0),
    [categories],
  );

  // O carrossel só renderiza quando a loja tem destaque; perguntamos ao DOM
  // em vez de repetir aqui o `pickFeatured()` dele — duas regras de "o que é
  // destaque" divergiriam no primeiro ajuste.
  const [temDestaques, setTemDestaques] = useState(false);
  useEffect(() => {
    const ver = () => setTemDestaques(!!document.getElementById('delivery-destaques'));
    ver();
    window.addEventListener('aliv:home-rendered', ver);
    return () => window.removeEventListener('aliv:home-rendered', ver);
  }, [categories.length]);

  // Acende a categoria que está na tela. `rootMargin` de -45% em cima e
  // -50% embaixo faz a faixa de detecção ser uma linha no meio da viewport —
  // sem isso, duas seções ficam "ativas" ao mesmo tempo no meio da rolagem.
  useEffect(() => {
    if (visiveis.length === 0) return;
    const secoes = visiveis
      .map((_, idx) => document.getElementById(`delivery-cat-${idx}`))
      .filter((el): el is HTMLElement => !!el);
    if (secoes.length === 0 || typeof IntersectionObserver === 'undefined') return;
    const destaques = document.getElementById('delivery-destaques');
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          if (el.id === 'delivery-destaques') { setAtiva(-1); continue; }
          const idx = Number(el.id.replace('delivery-cat-', ''));
          if (!Number.isNaN(idx)) setAtiva(idx);
        }
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );
    secoes.forEach((s) => obs.observe(s));
    if (destaques) obs.observe(destaques);
    return () => obs.disconnect();
  }, [visiveis.length, temDestaques]);

  // Mantém a categoria acesa visível na barra lateral (lista longa rolava
  // sozinha só no conteúdo, e a lateral ficava mostrando "Entradas" enquanto
  // o cliente já estava em "Sobremesas").
  useEffect(() => {
    const el = listaRef.current?.querySelector<HTMLElement>(`[data-cat-idx="${ativa}"]`);
    if (el && listaRef.current) {
      const box = listaRef.current.getBoundingClientRect();
      const alvo = el.getBoundingClientRect();
      if (alvo.top < box.top || alvo.bottom > box.bottom) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [ativa]);

  const filtradas = useMemo(() => {
    const q = normalizar(busca).trim();
    if (!q) return visiveis.map((c, idx) => ({ cat: c, idx }));
    return visiveis
      .map((c, idx) => ({ cat: c, idx }))
      // Busca no nome traduzido TAMBÉM: o cliente que está lendo "Desserts"
      // digita "dess", não "sobremesas".
      .filter(({ cat }) => normalizar(cat.category_name).includes(q) || normalizar(tr(cat.category_name)).includes(q));
  }, [visiveis, busca, tr]);

  if (visiveis.length === 0) return null;

  const ir = (idx: number) => {
    const el = document.getElementById(idx < 0 ? 'delivery-destaques' : `delivery-cat-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    onNavigate?.();
  };

  return (
    <div className="flex flex-col min-h-0">
      {comBusca && visiveis.length > 6 && (
        <div className="px-3 pb-3 shrink-0">
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={t('Buscar categoria...')}
              className="w-full bg-gray-100 border border-transparent focus:border-red-300 focus:bg-white rounded-full pl-9 pr-3 py-2 text-xs font-medium text-gray-700 outline-none transition"
            />
          </div>
        </div>
      )}

      <div ref={listaRef} className="overflow-y-auto min-h-0 px-2 pb-4 space-y-0.5">
        {/* Destaques fica FORA do filtro de busca: é um atalho fixo pro topo,
            não um resultado de "que categorias casam com o que digitei". */}
        {temDestaques && !busca.trim() && (
          <>
            <button
              type="button"
              data-cat-idx={-1}
              onClick={() => ir(-1)}
              aria-current={ativa === -1 ? 'true' : undefined}
              className={[
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition',
                ativa === -1
                  ? 'bg-amber-50 text-amber-700 font-bold'
                  : 'text-gray-600 hover:bg-gray-100 active:scale-[0.98] font-semibold',
              ].join(' ')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <i className={`fas fa-star text-xs w-4 text-center shrink-0 ${ativa === -1 ? 'text-amber-500' : 'text-yellow-400'}`} />
              <span className="text-[13px] leading-snug break-words">{t('Destaques')}</span>
            </button>
            <div className="h-px bg-gray-100 mx-3 my-1.5" />
          </>
        )}
        {filtradas.length === 0 && (
          <p className="text-xs text-gray-400 px-3 py-6 text-center">{t('Nenhuma categoria encontrada')}</p>
        )}
        {filtradas.map(({ cat, idx }) => {
          const on = idx === ativa;
          return (
            <button
              key={cat.category_id}
              type="button"
              data-cat-idx={idx}
              onClick={() => ir(idx)}
              aria-current={on ? 'true' : undefined}
              className={[
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition group',
                on
                  ? 'bg-red-50 text-red-700 font-bold'
                  : 'text-gray-600 hover:bg-gray-100 active:scale-[0.98] font-semibold',
              ].join(' ')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <i
                className={[
                  'fas fa-utensils text-xs w-4 text-center shrink-0',
                  on ? 'text-red-500' : 'text-gray-300 group-hover:text-gray-400',
                ].join(' ')}
              />
              <span className="text-[13px] leading-snug break-words">{tr(cat.category_name)}</span>
              <span className="ml-auto text-[10px] font-bold text-gray-300 tabular-nums shrink-0">
                {cat.items.length}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
